import {forumApi} from '@/api/ForumApi';
import he from 'he';
import {ratingApi, ReportOption} from '@/api/RatingApi';
import _ from 'lodash';
import {DateTime} from 'luxon';
import {Config} from '@/config';
import {map, shareReplay} from 'rxjs/operators';
import {Observable, zip} from 'rxjs';
import {LanguageCodes} from '@/api/TranslationsApi';
import {HeroesVersion} from '@/api/HeroesVersion';

class ReportsService {
  public getUnconfirmedReports(heroesVersion: HeroesVersion, lang: string) {
    return this.getFilledReports$(
      ratingApi.getUnconfirmedReports$(heroesVersion),
      lang,
    );
  }

  public parseOption(option: string) {
    return option.split('/').map((x) => x.trim());
  }

  public getUserReports$(userId: number, lang: string) {
    return this.getFilledReports$(
      ratingApi.getUserProfile$(userId).pipe(map((x: any) => x.reports)),
      lang,
    );
  }

  public getFilledReports$(reports: Observable<any>, lang: string) {
    return zip(
      reports,
      reportsService.getReportOptionsForLang$(lang),
      reportsService.getReportOptionsCategoryForLang$(LanguageCodes.English, ReportOption.Colors),
    ).pipe(
      map(([r, reportOptions, englishReportOptions]) =>
        reportsService.fillReportOptions(r, reportOptions, englishReportOptions)),
      map((r) => reportsService.markViolators(r)),
    );
  }

  public getReportOptionsForLang$(lang: string) {
    return this.getReportOptions$()
      .pipe(map((x) => x[lang]));
  }

  public getReportOptionsCategoryForLang$(lang: string, optionsCategory: string) {
    return this.getReportOptionsForLang$(lang)
      .pipe(map((x: any) => x[optionsCategory]));
  }

  public getReportOptions$() {
    return ratingApi.getReportOptions$()
      .pipe(
        map((responseData: any) => {
          const ruData: any = {};
          const enData: any = {};
          [
            ReportOption.Colors,
            ReportOption.Fractions,
            ReportOption.Results,
          ].forEach((cat) => {
            ruData[cat] = {};
            enData[cat] = {};
            for (const optionId of Object.keys(responseData[cat])) {
              const translation = responseData[cat][optionId];
              const translations = this.parseOption(translation);
              ruData[cat][optionId] = translations[0];
              enData[cat][optionId] = translations[1];
            }
          });

          ruData[ReportOption.Heroes] = {0: {}};
          enData[ReportOption.Heroes] = {0: {}};
          for (const fractionId of Object.keys(responseData[ReportOption.Heroes])) {
            ruData[ReportOption.Heroes][fractionId] = {};
            enData[ReportOption.Heroes][fractionId] = {};
            for (const heroId of Object.keys(responseData[ReportOption.Heroes][fractionId])) {
              const translation = responseData[ReportOption.Heroes][fractionId][heroId];
              const translations = this.parseOption(translation);
              ruData[ReportOption.Heroes][0][heroId] = translations[0];
              ruData[ReportOption.Heroes][fractionId][heroId] = translations[0];
              enData[ReportOption.Heroes][0][heroId] = translations[1];
              enData[ReportOption.Heroes][fractionId][heroId] = translations[1];
            }
          }

          ruData[ReportOption.Templates] = responseData[ReportOption.Templates];
          enData[ReportOption.Templates] = responseData[ReportOption.Templates];
          return {
            [LanguageCodes.English]: enData,
            [LanguageCodes.Russian]: ruData,
          };
        }),
        shareReplay(1),
      );
  }

  public fillReportOptions(reports: any, reportOptions: any, englishColorOptions: any) {
    return reports.map((x: any) => Object.assign(x, {
        player1_link: forumApi.getUserProfileUrl(x.player1.id),
        player2_link: forumApi.getUserProfileUrl(x.player2.id),
        player1_id: x.player1.id,
        player2_id: x.player2.id,
        player1_username: he.decode(x.player1.name),
        player2_username: he.decode(x.player2.name),
        player_fraction: reportOptions[ReportOption.Fractions][x.player1.fraction],
        player_fraction_random: x.player1.fraction_random === '1',
        player_start_hero: reportOptions[ReportOption.Heroes][0][x.player1.hero_start],
        player_start_hero_random: x.player1.start_hero_random === '1',
        player_main_hero: reportOptions[ReportOption.Heroes][0][x.player1.hero_main],
        player_color: englishColorOptions[x.player1.color],
        result: reportOptions[ReportOption.Results][x.game_result],
        template: reportOptions[ReportOption.Templates][x.game_template],
        rival_fraction: reportOptions[ReportOption.Fractions][x.player2.fraction],
        rival_fraction_random: x.player2.fraction_random === '1',
        rival_start_hero: reportOptions[ReportOption.Heroes][0][x.player2.hero_start],
        rival_start_hero_random: x.player2.start_hero_random === '1',
        rival_main_hero: reportOptions[ReportOption.Heroes][0][x.player2.hero_main],
        rival_color: englishColorOptions[x.player2.color],
        player1_forum_link: forumApi.getForumPostUrl(x.player1.post_id),
        player2_forum_link: forumApi.getForumPostUrl(x.player2.post_id),
      }),
    );
  }

  public isReportHasViolator(report: any, player1Reports: [], player2Reports: []) {
    const countLimit = Config.unconfirmedReportsViolationCount;
    const monthsLimit = Config.unconfirmedReportsViolationMonths;
    const filter = (x: any) =>
      DateTime.fromJSDate(new Date(x.game_date)) <
      DateTime.local().toUTC().minus({months: monthsLimit});
    return player1Reports.filter(filter).length > countLimit ||
      player2Reports.filter(filter).length > countLimit;
  }

  public markViolators(reports: any[]) {
    const self = this;
    const playerReports: any = _.merge(
      _.groupBy(_.cloneDeep(reports), (r) => r.player1_id),
      _.groupBy(_.cloneDeep(reports), (r) => r.player2_id),
    );
    return reports.map((x: any) => Object.assign(x, {
      has_violator: self.isReportHasViolator(
        x,
        playerReports[x.player1_id],
        playerReports[x.player2_id],
      ),
    }));
  }
}

export const reportsService = new ReportsService();
